import type {
  CompositionV3DataQuery,
  PostingsV3DataQuery,
} from '@revelio/data-access';

import {
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';
import { calculateMeterValue } from './z-score/calculate-meter-value';

type CompositionData = NonNullable<CompositionV3DataQuery['composition_v3']>;
type CompositionEntity = NonNullable<CompositionData['entities']>[number];

type PostingData = NonNullable<PostingsV3DataQuery['postings_v3']>;
type PostingEntity = NonNullable<PostingData['entities'][number]>;

/** ================================
 * Computation Functions
 ================================ */
/** Entry Point */
export const computeDemandSupplyRatio = ({
  compositionData,
  postingData,
  isDSRTimeseries,
  metric,
  weekRefs,
  monthRefs,
}: {
  compositionData: CompositionEntity[];
  postingData: PostingEntity[];
  isDSRTimeseries: boolean;
  metric: 'demand_supply' | 'demand_supply_percentile';
  weekRefs: ReadonlyMap<number, string>;
  monthRefs: ReadonlyMap<number, string>;
}): SingleDataSet | TimeseriesDataSet => {
  if (isDSRTimeseries) {
    return computeDemandSupplyTimeseries({
      headcounts: monthlyCompositionHeadcounts(compositionData, monthRefs),
      postingData,
      weekRefs,
      metric,
    });
  }

  return computeLatestDemandSupplyRatio({
    headcounts: latestCompositionHeadcounts(compositionData),
    postingData,
    metric,
  });
};

export const computeLatestDemandSupplyRatio = ({
  headcounts,
  postingData,
  metric,
}: {
  headcounts: ReadonlyMap<string, number>;
  postingData: PostingEntity[];
  metric: 'demand_supply' | 'demand_supply_percentile';
}): SingleDataSet => {
  const data = computeDSRMostRecent({ headcounts, postingData });
  return metric === 'demand_supply_percentile'
    ? computeDSRPercentileMostRecent(data.data)
    : data;
};

const latestCompositionHeadcounts = (
  compositionData: CompositionEntity[]
): Map<string, number> =>
  new Map(
    compositionData.flatMap(({ id, metrics }) => {
      const last = metrics.headcount[metrics.headcount.length - 1];
      return last ? [[String(id), last.val] as const] : [];
    })
  );

export const computeDemandSupplyTimeseries = ({
  headcounts,
  postingData,
  weekRefs,
  metric,
}: {
  headcounts: ReadonlyMap<string, ReadonlyMap<string, number>>;
  postingData: PostingEntity[];
  weekRefs: ReadonlyMap<number, string>;
  metric: 'demand_supply' | 'demand_supply_percentile';
}): TimeseriesDataSet => {
  const data = computeDSRTimeseries({ headcounts, postingData, weekRefs });
  return metric === 'demand_supply_percentile'
    ? computeDSRPercentileTimeseries(data.data)
    : data;
};

const monthlyCompositionHeadcounts = (
  compositionData: CompositionEntity[],
  monthRefs: ReadonlyMap<number, string>
): Map<string, Map<string, number>> =>
  new Map(
    compositionData.map(({ id, metrics }) => [
      String(id),
      new Map(
        metrics.headcount.flatMap(({ id: monthId, val }) => {
          const month = monthRefs.get(monthId);
          return month === undefined ? [] : [[month, val] as const];
        })
      ),
    ])
  );

/** Timeseries Computation */
const computeDSRTimeseries = ({
  headcounts,
  postingData,
  weekRefs,
}: {
  headcounts: ReadonlyMap<string, ReadonlyMap<string, number>>;
  postingData: PostingEntity[];
  weekRefs: ReadonlyMap<number, string>;
}): TimeseriesDataSet => {
  const timeseriesData: TimeseriesData[] = [];

  for (const postingEntity of postingData) {
    const postingId = postingEntity.id;
    const headcountByMonth = headcounts.get(String(postingId));
    if (!headcountByMonth) continue;

    const timeseries: TimeseriesDataPoint[] = [];

    for (const active of postingEntity.metrics.active) {
      const week = weekRefs.get(active.id);
      if (!week) continue;

      const headcount = headcountByMonth.get(week.slice(0, 7));
      if (headcount === undefined) continue;

      timeseries.push({
        date: week,
        value: computeDSRValue(active.val, headcount),
      });
    }

    timeseriesData.push({
      id: postingId.toString(),
      dim: postingEntity?.short_name ?? postingEntity?.long_name,
      timeseries,
    });
  }

  return { type: DataType.TIMESERIES, data: timeseriesData };
};

const computeDSRPercentileTimeseries = (
  data: TimeseriesData[]
): TimeseriesDataSet => ({
  type: DataType.TIMESERIES,
  data: data.map((item) => ({
    ...item,
    timeseries: item.timeseries.map(getDSRPointPercentile),
  })),
});

/** Most Recent Computation */
const computeDSRMostRecent = ({
  headcounts,
  postingData,
}: {
  headcounts: ReadonlyMap<string, number>;
  postingData: PostingEntity[];
}): SingleDataSet => {
  const data: SingleDataSet['data'] = [];

  for (const postingEntity of postingData) {
    const postingId = postingEntity.id;
    const headcount = headcounts.get(String(postingId));
    const activePostings = postingEntity.metrics.active;
    const lastActive = activePostings[activePostings.length - 1];

    if (headcount === undefined || !lastActive) continue;

    data.push({
      id: postingId.toString(),
      dim: postingEntity?.short_name ?? postingEntity?.long_name,
      value: computeDSRValue(lastActive.val, headcount),
    });
  }

  return { type: DataType.SINGLE, data };
};

const computeDSRPercentileMostRecent = (
  data: SingleDataPoint[]
): SingleDataSet => ({
  type: DataType.SINGLE,
  data: data.map(getDSRPointPercentile),
});

/** ================================
 * Helper Functions
 ================================ */
const computeDSRValue = (postings: number, headcount: number): number =>
  headcount > 0 ? postings / headcount : 0;

export const mapWeekRefsToMonthRefs = (
  weekRefs: ReadonlyMap<number, string>,
  monthRefs: ReadonlyMap<number, string>
): Map<number, number> => {
  const monthToId = new Map(
    Array.from(monthRefs, ([monthId, month]) => [month, monthId])
  );

  return new Map(
    Array.from(weekRefs).flatMap(([weekId, week]) => {
      const month = week.slice(0, 7);
      const monthId = monthToId.get(month);
      return monthId === undefined ? [] : [[weekId, monthId]];
    })
  );
};

const getDSRPointPercentile = <T extends SingleDataPoint | TimeseriesDataPoint>(
  point: T
): T => ({
  ...point,
  value: calculateMeterValue({
    value: point.value ?? 0,
    metric: 'demandSupply',
  }),
});
